import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { HMIField, HMIFieldKind, HMIScreenKey, HMITemplate } from '../../types';
import {
  addField,
  addScreen,
  addSection,
  buildScreenChain,
  deleteField,
  deleteScreen,
  deleteSection,
  fieldCountFor,
  fieldsForGroup,
  fieldsUnderScreen,
  groupsForScreen,
  leafScreenKey,
  renameScreen,
  renameSection,
  reorderField,
  reorderScreen,
  reorderSection,
  resizeSection,
  ROOT_SCREEN_KEY,
  sectionsUnderScreen,
  updateField,
} from '../../layoutUtils';
import SectionBox from './SectionBox';
import ScreenTabStrip from './ScreenTabStrip';
import FieldEditorDialog, { FieldDraft } from './FieldEditorDialog';

export interface HmiLayoutBuilderProps {
  template: HMITemplate;
  readOnly?: boolean;
  onChange: (next: HMITemplate) => void;
}

type PendingAction =
  | { kind: 'addScreen'; parentKey?: HMIScreenKey }
  | { kind: 'renameScreen'; key: HMIScreenKey; label: string }
  | { kind: 'deleteScreen'; key: HMIScreenKey }
  | { kind: 'addSection'; screen: HMIScreenKey }
  | { kind: 'renameSection'; key: string; label: string }
  | { kind: 'deleteSection'; key: string }
  | { kind: 'addField'; screen: HMIScreenKey; group: string; defaultKind?: HMIFieldKind }
  | { kind: 'editField'; field: HMIField }
  | { kind: 'deleteField'; field: HMIField }
  | null;

/**
 * Visual, no-JSON builder for a `HMITemplate`: define tabs (screens) and, on
 * any tab, nest subtabs as deep as needed (`buildScreenChain` walks the
 * resulting tree); add/size sections on each leaf tab's 12-column grid; and
 * add/edit/delete that section's fields and widgets/graphs. The live
 * `HMIRenderer` honours the exact same tab tree and each group's
 * `layout.width` / `layout.height`.
 */
function HmiLayoutBuilder({ template, readOnly, onChange }: HmiLayoutBuilderProps) {
  // Remembers which tab is active at each level of the tab/subtab tree, keyed
  // by that level's parent tab (or ROOT_SCREEN_KEY for the top-level row).
  const [selection, setSelection] = useState<Record<string, HMIScreenKey>>({});
  const [pending, setPending] = useState<PendingAction>(null);
  const [draftLabel, setDraftLabel] = useState('');

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [canvasWidthPx, setCanvasWidthPx] = useState(0);

  const chain = useMemo(() => buildScreenChain(template, selection), [template, selection]);
  const activeScreen = leafScreenKey(chain);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setCanvasWidthPx(width);
    });
    observer.observe(el);
    setCanvasWidthPx(el.clientWidth);
    return () => observer.disconnect();
  }, [activeScreen]);

  const sections = useMemo(
    () => (activeScreen ? groupsForScreen(template, activeScreen) : []),
    [template, activeScreen]
  );

  const closePending = () => {
    setPending(null);
    setDraftLabel('');
  };

  const openRenameScreen = (key: HMIScreenKey, label: string) => {
    setDraftLabel(label);
    setPending({ kind: 'renameScreen', key, label });
  };
  const openRenameSection = (key: string, label: string) => {
    setDraftLabel(label);
    setPending({ kind: 'renameSection', key, label });
  };

  const handleSaveField = (draft: FieldDraft) => {
    if (!pending) return;
    if (pending.kind === 'addField') {
      onChange(addField(template, pending.screen, pending.group, draft));
    } else if (pending.kind === 'editField') {
      onChange(updateField(template, pending.field.id, draft));
    }
    closePending();
  };

  const handleDeleteFieldConfirm = () => {
    if (pending?.kind === 'deleteField') {
      onChange(deleteField(template, pending.field.id));
    } else if (pending?.kind === 'editField') {
      onChange(deleteField(template, pending.field.id));
    }
    closePending();
  };

  const confirmPending = () => {
    if (!pending) return;
    switch (pending.kind) {
      case 'addScreen': {
        if (!draftLabel.trim()) break;
        const next = addScreen(template, draftLabel, pending.parentKey);
        const newKey = next.screens[next.screens.length - 1].key;
        onChange(next);
        setSelection((prev) => ({ ...prev, [pending.parentKey ?? ROOT_SCREEN_KEY]: newKey }));
        break;
      }
      case 'renameScreen':
        onChange(renameScreen(template, pending.key, draftLabel));
        break;
      case 'deleteScreen':
        onChange(deleteScreen(template, pending.key));
        break;
      case 'addSection':
        if (!draftLabel.trim()) break;
        onChange(addSection(template, pending.screen, draftLabel));
        break;
      case 'renameSection':
        onChange(renameSection(template, pending.key, draftLabel));
        break;
      case 'deleteSection':
        onChange(deleteSection(template, pending.key));
        break;
      default:
        break;
    }
    closePending();
  };

  if (template.screens.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No tabs yet. Add one to start laying out the HMI.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={readOnly}
          onClick={() => setPending({ kind: 'addScreen' })}
        >
          Add tab
        </Button>
        {renderDialog()}
      </Box>
    );
  }

  return (
    <Box>
      {/* Tab / subtab columns, vertical and side-by-side (one column per level of the
          tab tree - each tab able to grow its own subtabs), beside the canvas. */}
      <Stack direction="row" alignItems="flex-start" spacing={0}>
        {chain.map((level) => (
          <ScreenTabStrip
            key={level.parentKey ?? ROOT_SCREEN_KEY}
            options={level.options}
            activeKey={level.activeKey}
            isRoot={level.parentKey === undefined}
            readOnly={readOnly}
            onSelect={(key) =>
              setSelection((prev) => ({ ...prev, [level.parentKey ?? ROOT_SCREEN_KEY]: key }))
            }
            onMove={(key, direction) => onChange(reorderScreen(template, key, direction))}
            onRename={openRenameScreen}
            onDelete={(key) => setPending({ kind: 'deleteScreen', key })}
            onAddSubtab={(parentKey) => {
              setDraftLabel('');
              setPending({ kind: 'addScreen', parentKey });
            }}
            onAddSibling={() => {
              setDraftLabel('');
              setPending({ kind: 'addScreen', parentKey: level.parentKey });
            }}
          />
        ))}

        {/* Canvas */}
        <Box
          ref={canvasRef}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            ml: 2,
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 1.5,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            minHeight: 160,
          }}
        >
          {sections.map((section, idx) => (
            <SectionBox
              key={section.key}
              label={section.label}
              width={section.layout?.width ?? 6}
              height={section.layout?.height ?? 4}
              fields={fieldsForGroup(template, section.key)}
              readOnly={readOnly}
              canMoveUp={idx > 0}
              canMoveDown={idx < sections.length - 1}
              containerWidthPx={canvasWidthPx}
              onResize={(size) => onChange(resizeSection(template, section.key, size))}
              onRename={() => openRenameSection(section.key, section.label)}
              onDelete={() => setPending({ kind: 'deleteSection', key: section.key })}
              onAddField={() =>
                setPending({ kind: 'addField', screen: activeScreen ?? '', group: section.key })
              }
              onAddWidget={() =>
                setPending({
                  kind: 'addField',
                  screen: activeScreen ?? '',
                  group: section.key,
                  defaultKind: 'chart',
                })
              }
              onAddImage={() =>
                setPending({
                  kind: 'addField',
                  screen: activeScreen ?? '',
                  group: section.key,
                  defaultKind: 'image',
                })
              }
              onEditField={(field) => setPending({ kind: 'editField', field })}
              onDeleteField={(field) => setPending({ kind: 'deleteField', field })}
              onMoveField={(field, direction) =>
                onChange(reorderField(template, section.key, field.id, direction))
              }
              onMove={(direction) =>
                onChange(
                  reorderSection(template, activeScreen ?? '', section.key, direction)
                )
              }
            />
          ))}

          {!readOnly && activeScreen && (
            <Box
              onClick={() => setPending({ kind: 'addSection', screen: activeScreen })}
              sx={{
                gridColumn: 'span 4',
                minHeight: 96,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              <AddIcon />
              <Typography variant="caption">Add section</Typography>
            </Box>
          )}

          {sections.length === 0 && readOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              No sections on this tab yet.
            </Typography>
          )}
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Use a tab&apos;s "Add subtab" icon to nest subtabs under it. Drag a section&apos;s
        bottom-right corner to resize it (columns x rows). Use a section&apos;s "Field" /
        "Widget" buttons to add, edit, reorder, or delete its fields.
      </Typography>

      {renderDialog()}
    </Box>
  );

  function renderDialog() {
    if (!pending) return null;

    if (pending.kind === 'addField' || pending.kind === 'editField') {
      return (
        <FieldEditorDialog
          field={pending.kind === 'editField' ? pending.field : undefined}
          defaultKind={pending.kind === 'addField' ? pending.defaultKind : undefined}
          template={template}
          onCancel={closePending}
          onSave={handleSaveField}
          onDelete={pending.kind === 'editField' ? handleDeleteFieldConfirm : undefined}
        />
      );
    }

    if (pending.kind === 'deleteField') {
      return (
        <Dialog open onClose={closePending}>
          <DialogTitle>Delete &quot;{pending.field.caption}&quot;?</DialogTitle>
          <DialogContent>
            <Alert severity="warning">This cannot be undone.</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePending}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeleteFieldConfirm}>
              Delete field
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    if (pending.kind === 'deleteScreen') {
      const fieldCount = fieldsUnderScreen(template, pending.key).length;
      const sectionCount = sectionsUnderScreen(template, pending.key).length;
      return (
        <Dialog open onClose={closePending}>
          <DialogTitle>Delete this tab?</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              This also deletes {sectionCount} section{sectionCount === 1 ? '' : 's'} and{' '}
              {fieldCount} field{fieldCount === 1 ? '' : 's'} on this tab. This cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePending}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmPending}>
              Delete tab
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    if (pending.kind === 'deleteSection') {
      const fieldCount = fieldCountFor(template, activeScreen ?? '', pending.key);
      return (
        <Dialog open onClose={closePending}>
          <DialogTitle>Delete this section?</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              This also deletes {fieldCount} field{fieldCount === 1 ? '' : 's'} assigned to it.
              This cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePending}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmPending}>
              Delete section
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    const titles: Record<string, string> = {
      addScreen: pending.kind === 'addScreen' && pending.parentKey ? 'Add subtab' : 'Add tab',
      renameScreen: 'Rename tab',
      addSection: 'Add section',
      renameSection: 'Rename section',
    };

    return (
      <Dialog open onClose={closePending} fullWidth maxWidth="xs">
        <DialogTitle>{titles[pending.kind]}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Label"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmPending();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePending}>Cancel</Button>
          <Button variant="contained" disabled={!draftLabel.trim()} onClick={confirmPending}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default HmiLayoutBuilder;
